import { useState, useEffect, useCallback } from 'react'
import api from '../lib/api'
import { SyncProgress } from './SyncProgress'
import { CategoriesEditor } from './CategoriesEditor'

export function AccountList({ socket }) {
  const [editingCategoriesFor, setEditingCategoriesFor] = useState(null)
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [syncingAccounts, setSyncingAccounts] = useState({})
  const [error, setError] = useState('')
  const [clearingContacts, setClearingContacts] = useState({})
  const [mailboxes, setMailboxes] = useState({})
  const [selectedMailboxes, setSelectedMailboxes] = useState({})
  const [loadingMailboxes, setLoadingMailboxes] = useState({})
  const [selectedSyncOption, setSelectedSyncOption] = useState({})

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await api.get('/imap-accounts')
      setAccounts(res.data)
    } catch (err) {
      setError('Failed to load accounts')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAccounts()
  }, [fetchAccounts])

  useEffect(() => {
    if (!socket) return

    socket.on('job-progress', (data) => {
      setSyncingAccounts((prev) => ({
        ...prev,
        [data.jobId]: data,
      }))
    })

    socket.on('job-complete', (data) => {
      setSyncingAccounts((prev) => {
        const updated = { ...prev }
        delete updated[data.jobId]
        return updated
      })
      fetchAccounts()
    })

    socket.on('job-failed', (data) => {
      setSyncingAccounts((prev) => {
        const updated = { ...prev }
        delete updated[data.jobId]
        return updated
      })
      fetchAccounts()
    })

    return () => {
      socket.off('job-progress')
      socket.off('job-complete')
      socket.off('job-failed')
    }
  }, [socket, fetchAccounts])

  const loadMailboxes = async (accountId) => {
    if (mailboxes[accountId]) return
    setLoadingMailboxes((prev) => ({ ...prev, [accountId]: true }))
    try {
      const res = await api.get(`/imap-accounts/${accountId}/mailboxes`)
      setMailboxes((prev) => ({ ...prev, [accountId]: res.data.mailboxes }))
      setSelectedMailboxes((prev) => ({ ...prev, [accountId]: 'INBOX' }))
    } catch (err) {
      setError('Failed to load mailboxes')
    } finally {
      setLoadingMailboxes((prev) => ({ ...prev, [accountId]: false }))
    }
  }

  const buildSyncPayload = (account, optionValue) => {
    const hasHistory = Boolean(account.lastEmailDate)
    if (!hasHistory) {
      // Initial sync — option encodes the date range.
      return { mode: 'initial', range: optionValue || 'all' }
    }
    if (optionValue === 'forward') return { mode: 'forward' }
    if (optionValue === 'full') {
      if (!window.confirm('Full re-sync wipes the known date window and refetches all emails. Continue?')) {
        return null
      }
      return { mode: 'full', range: 'all' }
    }
    if (optionValue && optionValue.startsWith('backward:')) {
      return { mode: 'backward', range: optionValue.split(':')[1] }
    }
    return { mode: 'forward' }
  }

  const handleStartSync = async (account) => {
    setError('')
    const accountId = account._id
    const mailbox = selectedMailboxes[accountId] || 'INBOX'
    const option = selectedSyncOption[accountId] || (account.lastEmailDate ? 'forward' : 'all')
    const payload = buildSyncPayload(account, option)
    if (!payload) return
    try {
      const res = await api.post('/sync-jobs/start', { accountId, mailbox, ...payload })
      socket.emit('subscribe-job', res.data.jobId)
      setSyncingAccounts((prev) => ({
        ...prev,
        [res.data.jobId]: {
          jobId: res.data.jobId,
          totalEmails: 0,
          fetchedEmails: 0,
          insertedEmails: 0,
          status: 'pending',
        },
      }))
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start sync')
    }
  }

  const handleClearData = async (accountId, email) => {
    if (!window.confirm(`Clear ALL synced data (emails, contacts, sync history) for "${email}"? The account itself will be kept. This cannot be undone.`)) {
      return
    }
    setError('')
    try {
      const res = await api.delete(`/imap-accounts/${accountId}/data`)
      const d = res.data?.deleted || {}
      alert(`Cleared: ${d.emails || 0} emails, ${d.contacts || 0} contacts, ${d.syncJobs || 0} sync jobs.`)
      fetchAccounts()
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to clear account data')
    }
  }
  
  const handleClearContacts = async (accountId, email) => {
    if (!window.confirm(`Permanently delete all contacts for "${email}"?\n\nEmails will be kept but marked for re-processing so contacts can be rebuilt. This cannot be undone.`)) return
    setError('')
    setClearingContacts((prev) => ({ ...prev, [accountId]: true }))
    try {
      await api.delete(`/imap-accounts/${accountId}/contacts`)
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to clear contacts')
    } finally {
      setClearingContacts((prev) => ({ ...prev, [accountId]: false }))
    }
  }

  const handleDeleteAccount = async (accountId, email) => {
    if (!window.confirm(`Delete account "${email}" and all its synced emails? This cannot be undone.`)) {
      return
    }
    setError('')
    try {
      await api.delete(`/imap-accounts/${accountId}`)
      fetchAccounts()
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete account')
    }
  }

  if (loading) return <div className="loading">Loading accounts...</div>

  return (
    <div className="account-list">
      {error && <div className="message error">{error}</div>}

      {accounts.length === 0 ? (
        <p className="empty-state">No IMAP accounts added yet.</p>
      ) : (
        accounts.map((account) => (
          <div key={account._id} className="account-card">
            <div className="account-info">
              <h3>{account.email}</h3>
              <p className="account-meta">
                {account.host}:{account.port} {account.secure ? '(SSL)' : '(No SSL)'}
              </p>
              <span className={`status-badge status-${account.status}`}>{account.status}</span>
              {account.lastSyncAt && (
                <p className="last-sync">Last synced: {new Date(account.lastSyncAt).toLocaleString()}</p>
              )}
            </div>

            <div className="account-actions">
              <button
                onClick={() => loadMailboxes(account._id)}
                className="btn btn-outline"
              >
                {loadingMailboxes[account._id] ? 'Loading...' : 'Load Mailboxes'}
              </button>
              <SyncControls
                account={account}
                value={selectedSyncOption[account._id]}
                onChange={(v) => setSelectedSyncOption((prev) => ({ ...prev, [account._id]: v }))}
                onStart={() => handleStartSync(account)}
              />
              <button
                onClick={() => setEditingCategoriesFor(editingCategoriesFor === account._id ? null : account._id)}
                className="btn btn-outline"
              >
                {editingCategoriesFor === account._id ? 'Hide Categories' : 'Categories'}
              </button>
              <button
                onClick={() => handleClearData(account._id, account.email)}
                className="btn btn-outline"
              >
                Clear Data
              </button>
              <button 
                onClick={() => handleClearContacts(account._id, account.email)}
                className="btn btn-outline btn-warning"
                disabled={clearingContacts[account._id]}
              >
                {clearingContacts[account._id] ? 'Clearing…' : 'Clear Contacts'}
              </button>
              <button
                onClick={() => handleDeleteAccount(account._id, account.email)}
                className="btn btn-danger"
              >
                Delete
              </button>
            </div>

            {editingCategoriesFor === account._id && (
              <div className="categories-panel">
                <CategoriesEditor
                  accountId={account._id}
                  onClose={() => setEditingCategoriesFor(null)}
                />
              </div>
            )}

            {mailboxes[account._id] && (
              <div className="mailbox-selector">
                <label>Mailbox:</label>
                <select
                  value={selectedMailboxes[account._id] || 'INBOX'}
                  onChange={(e) => setSelectedMailboxes((prev) => ({ ...prev, [account._id]: e.target.value }))}
                >
                  {mailboxes[account._id].map((mb) => (
                    <option key={mb.path} value={mb.path}>
                      {mb.name} ({mb.path})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {(account.firstEmailDate || account.lastEmailDate) && (
              <p className="sync-window">
                Synced:{' '}
                {account.firstEmailDate ? new Date(account.firstEmailDate).toLocaleDateString() : '—'}
                {' → '}
                {account.lastEmailDate ? new Date(account.lastEmailDate).toLocaleDateString() : '—'}
              </p>
            )}

            {Object.values(syncingAccounts)
              .filter((job) => job.accountId === account._id || true)
              .map((job) => (
                <SyncProgress key={job.jobId} job={job} socket={socket} />
              ))}
          </div>
        ))
      )}
    </div>
  )
}

function SyncControls({ account, value, onChange, onStart }) {
  const hasHistory = Boolean(account.lastEmailDate)
  const isSyncing = account.status === 'syncing'

  const initialOptions = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '3m', label: 'Last 3 months' },
    { value: '1y', label: 'Last 1 year' },
    { value: 'all', label: 'All time' },
  ]

  const incrementalOptions = [
    { value: 'forward', label: 'Sync new emails only' },
    { value: 'backward:3m', label: 'Extend back: 3 more months' },
    { value: 'backward:1y', label: 'Extend back: 1 more year' },
    { value: 'backward:all', label: 'Extend back: all remaining' },
    { value: 'full', label: 'Full re-sync (wipe & refetch)' },
  ]

  const options = hasHistory ? incrementalOptions : initialOptions
  const defaultValue = hasHistory ? 'forward' : 'all'
  const current = value || defaultValue

  return (
    <div className="sync-controls">
      <select
        value={current}
        onChange={(e) => onChange(e.target.value)}
        disabled={isSyncing}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <button
        onClick={onStart}
        disabled={isSyncing}
        className="btn btn-secondary"
      >
        {isSyncing ? 'Syncing...' : 'Start Sync'}
      </button>
    </div>
  )
}
